import { FC, useCallback, useRef, useState } from 'react';
import {
  WbAlert,
  WbButton,
  WbDrawerActions,
  WbDrawerContent,
  WbTextField,
  useWbDrawerContext,
  customAlertApiRef,
  UsersAndGroupsPicker,
  AvailableEntities,
  WbEntityRefLink,
} from '@agilelab/plugin-wb-platform';
import { Controller, useForm, SubmitHandler } from 'react-hook-form';
import { FormControl, Typography } from '@material-ui/core';
import { useApi } from '@mainstage/core-plugin-api';
import {
  useUpsertServiceAccount,
  useWatchServiceAccount,
} from '@agilelab/backstage-plugin-wb-service-accounts';
import { Entity } from '@mainstage/catalog-model';
import { ServiceAccountBase } from '@agilelab/plugin-wb-service-accounts-common';

type ServiceAccountProfile = {
  email?: string;
  displayName?: string;
};

export const EditServiceAccountForm: FC<{
  serviceAccount: Entity;
  onEdited?: () => void;
}> = ({ serviceAccount, onEdited }) => {
  const { setOpen } = useWbDrawerContext();
  const alertApi = useApi(customAlertApiRef);

  const form = useForm<ServiceAccountBase>({
    defaultValues: {
      email:
        (serviceAccount?.spec?.profile as ServiceAccountProfile)?.email || '',
      displayName:
        (serviceAccount?.spec?.profile as ServiceAccountProfile)?.displayName ||
        '',
      groups: serviceAccount?.relations
        ?.filter(e => e.type === 'memberOf')
        ?.map(e => e.targetRef),
    },
    mode: 'onChange',
  });

  const {
    isLoading: editing,
    error: editError,
    mutate: upsertFn,
  } = useUpsertServiceAccount({ alertOnError: false });

  const [genericError, setGenericError] = useState<string | null>(null);

  const formRef = useRef<HTMLFormElement>(null);

  const handleProgrammaticSubmit = useCallback(() => {
    formRef.current?.requestSubmit();
  }, []);

  const { watchServiceAccount } = useWatchServiceAccount();
  const watchServiceAccountRef = useRef<ZenObservable.Subscription | null>(
    null,
  );

  const onSubmit: SubmitHandler<ServiceAccountBase> = useCallback(
    data => {
      setGenericError(null);
      upsertFn(data, {
        onSuccess: res => {
          const { ref, timestamp } = res;
          const observable = watchServiceAccount({
            ref,
            after: timestamp,
            timeout: 30000,
          });
          // reset observer if already exists
          watchServiceAccountRef.current?.unsubscribe();

          watchServiceAccountRef.current = observable.subscribe({
            next: ev => {
              switch (ev.status) {
                case 'found':
                  setOpen(false);
                  alertApi.post({
                    severity: 'success',
                    message: 'Service Account edited successfully',
                    actions: (
                      <>
                        <WbEntityRefLink
                          entityRef={ev.value}
                          title="View edited Service Account"
                        />
                      </>
                    ),
                  });
                  onEdited?.();
                  break;
                case 'timeout':
                  setGenericError('Service Account creation timeout');
                  break;
                case 'invalid-entity':
                  setGenericError('Invalid entity ref');
                  break;
                case 'not-found':
                  break;
                default:
                  break;
              }
            },
            error: err => {
              setGenericError(
                (err instanceof Error ? err : new Error('Something went wrong'))
                  .message,
              );
            },
            complete: () => {
              // reset observer
              watchServiceAccountRef.current = null;
            },
          });
        },
      });
    },
    [alertApi, onEdited, setOpen, upsertFn, watchServiceAccount],
  );

  const isValid = form.formState.isValid;
  const loading =
    editing ||
    form.formState.isValidating ||
    watchServiceAccountRef.current !== null;

  return (
    <>
      <WbDrawerContent>
        <form ref={formRef} onSubmit={form.handleSubmit(onSubmit)}>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 16,
              marginTop: 8,
            }}
          >
            {editError instanceof Error && (
              <WbAlert
                variant="collapsible"
                message={{
                  title: 'Error',
                  type: 'error',
                  content: <Typography>{editError.message}</Typography>,
                }}
              />
            )}
            {genericError && (
              <WbAlert
                variant="collapsible"
                message={{
                  title: 'Error',
                  type: 'error',
                  content: <Typography>{genericError}</Typography>,
                }}
              />
            )}
            <Controller
              name="email"
              control={form.control}
              rules={{
                required: 'Email is required',
                pattern: {
                  value: /[^@\s]+@[^@\s]+\.[^@\s]+/,
                  message: 'Enter a valid email',
                },
              }}
              render={({ field, fieldState: { error } }) => (
                <FormControl>
                  <WbTextField
                    label="Email"
                    id="email"
                    required
                    disabled
                    value={field.value}
                    error={!!error}
                    onChange={field.onChange}
                    helperText={error?.message}
                    style={{ width: '100%' }}
                  />
                </FormControl>
              )}
            />

            <Controller
              name="displayName"
              control={form.control}
              render={({ field, fieldState: { error } }) => (
                <FormControl>
                  <WbTextField
                    label="Display name"
                    id="displayName"
                    value={field.value || ''}
                    error={!!error}
                    onChange={e => {
                      field.onChange(e.target.value || undefined);
                    }}
                    helperText={error?.message}
                    style={{ width: '100%' }}
                  />
                </FormControl>
              )}
            />

            <Controller
              name="groups"
              control={form.control}
              render={({ field }) => (
                <FormControl>
                  <UsersAndGroupsPicker
                    title="Groups"
                    multiSelection
                    value={field.value || []}
                    onSelect={v => field.onChange(v)}
                    allowedKinds={[AvailableEntities.GROUP]}
                  />
                </FormControl>
              )}
            />
          </div>
        </form>
      </WbDrawerContent>
      <WbDrawerActions>
        <WbButton
          onClick={() => setOpen(false)}
          disabled={editing}
          variant="outlined"
          color="primary"
          label="Cancel"
        />
        <WbButton
          label="Save"
          onClick={handleProgrammaticSubmit}
          disabled={!isValid}
          loading={loading}
          variant="contained"
          color="primary"
        />
      </WbDrawerActions>
    </>
  );
};

EditServiceAccountForm.displayName = 'EditServiceAccountForm';
